<template>
  <v-card flat>
    <v-card-text class="pt-4">
      <v-form ref="form" v-model="isValid" @submit.prevent="submitForm">
        <v-row dense>
          <!-- انتخاب محصول دیجی‌کالا -->
          <v-col cols="12">
            <v-autocomplete
              ref="productSelect"
              v-model="payload.digikala_product_id"
              :items="digikalaProducts"
              :loading="isLoadingProducts"
              :search-input.sync="searchProduct"
              item-value="id"
              item-text="title"
              label="محصول دیجی‌کالا (جستجو با نام یا DKP)"
              outlined
              dense
              clearable
              required
              :rules="[v => !!v || 'انتخاب محصول دیجی‌کالا الزامی است']"
              prepend-inner-icon="mdi-magnify"
              placeholder="نام یا کد محصول دیجی‌کالا را تایپ کنید..."
              no-filter
              @change="handleProductChange"
            >
              <!-- حالت انتخاب شده -->
              <template v-slot:selection="{ item }">
                <div class="d-flex align-center text-truncate py-1">
                  <v-avatar size="28" rounded="sm" class="ml-2 border flex-shrink-0" color="grey lighten-4">
                    <v-img v-if="getItemImage(item)" :src="getItemImage(item)" contain />
                    <v-icon v-else x-small color="grey lighten-1">mdi-image-outline</v-icon>
                  </v-avatar>
                  <v-chip x-small color="primary" class="ml-2 font-weight-bold">DKP-{{ item.id }}</v-chip>
                  <span class="text-truncate">{{ item.title }}</span>
                </div>
              </template>

              <!-- آیتم‌های لیست بازشونده -->
              <template v-slot:item="{ item }">
                <div class="d-flex align-center py-2 w-100">
                  <v-avatar size="44" rounded="lg" class="ml-3 border flex-shrink-0" color="grey lighten-4">
                    <v-img v-if="getItemImage(item)" :src="getItemImage(item)" contain />
                    <v-icon v-else small color="grey lighten-1">mdi-image-outline</v-icon>
                  </v-avatar>
                  <v-list-item-content class="py-0">
                    <v-list-item-title class="font-weight-medium text-body-2 text-wrap" style="line-height: 1.4;">
                      {{ item.title }}
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-caption mt-1 d-flex align-center">
                      <v-chip x-small label outlined color="primary" class="font-mono">
                        کد کالا: DKP-{{ item.id }}
                      </v-chip>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </div>
              </template>
            </v-autocomplete>

          </v-col>

          <v-col cols="12">
            <div class="d-flex flex-row justify-space-between align-center mb-2">
              <div class="subtitle-2 text-primary font-weight-bold">
                <v-icon small color="primary" class="ml-1">mdi-cogs</v-icon>
                <span>تنظیمات پیش‌فرض محصول</span>
              </div>
              <PriceCalculator
                class="mr-2"
                :product-id="payload.product_id"
                :form-data="payload"
              />
            </div>
            <v-alert
              border="left"
              color="info"
              colored-border
              class="text-body-2 mb-3"
              elevation="1"
              icon="mdi-information"
            >
              مقادیر وارد شده در بالا به عنوان پیش‌فرض برای تمامی تنوع‌ها در نظر گرفته می‌شوند. در صورتی که برای هر تنوع مقادیر اختصاصی وارد کنید، آن مقادیر جایگزین تنظیمات پیش‌فرض خواهند شد.
            </v-alert>
          </v-col>

          <!-- تعداد در بسته کلی -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="payload.package_quantity"
              label="تعداد در بسته"
              outlined
              dense
              type="number"
              min="1"
              required
              :rules="[
                v => !!v || 'وارد کردن تعداد در بسته الزامی است',
                v => v > 0 || 'تعداد باید بیشتر از صفر باشد'
              ]"
              prepend-inner-icon="mdi-package-variant-closed"
            />
          </v-col>

          <!-- روش ارسال کلی -->
          <v-col cols="12" md="6">
            <v-select
              v-model="payload.shipping_method"
              label="روش ارسال"
              :items="shipping_methods"
              outlined
              dense
              style="border-radius: 8px"
              :menu-props="{ offsetY: true }"
            />
          </v-col>

          <!-- هزینه هر بسته کلی -->
          <v-col cols="12" md="6">
            <PriceInput
              v-model="payload.package_cost"
              label="هزینه هر بسته"
              prepend-inner-icon="mdi-currency-usd"
              outlined
              dense
              :rules="[v => !!v || 'وارد کردن هزینه هر بسته الزامی است']"
            />
          </v-col>

          <!-- گام قیمت کلی -->
          <v-col cols="12" md="6">
            <PriceInput
              v-model="payload.price_step"
              label="گام قیمت"
              prepend-inner-icon="mdi-step"
              outlined
              dense
              :rules="[v => !!v || 'وارد کردن گام قیمت الزامی است']"
            />
          </v-col>
        </v-row>

        <!-- بخش تنوع‌ها به صورت Tab -->
        <template v-if="product.has_variants">
          <v-divider class="my-4"></v-divider>
          <div class="subtitle-1 font-weight-bold mb-3 text-primary d-flex align-center">
            <v-icon left color="primary">mdi-sitemap</v-icon>
            تنظیم و تطابق تنوع‌ها
          </div>

          <v-alert v-if="skusLoading" type="info" text dense class="mb-3">
            در حال دریافت تنوع‌های داخلی محصول...
          </v-alert>

          <v-alert v-else-if="!localSkus.length" type="warning" text dense class="mb-3">
            هیچ تنوعی برای این محصول یافت نشد!
          </v-alert>

          <div v-else>
            <v-tabs
              v-model="activeSkuTab"
              background-color="primary"
              color="white"
              dark
              class="rounded-t-lg custom-tabs"
              show-arrows
              height="52"
              grow
            >
              <v-tab
                v-for="(sku, index) in localSkus"
                :key="sku.id"
                class="px-3"
              >
                <div class="d-flex align-center">
                  <v-avatar
                    size="26"
                    :color="getSkuStatus(sku).bgColor"
                    class="ml-2"
                  >
                    <v-icon x-small :color="getSkuStatus(sku).color">
                      {{ getSkuStatus(sku).icon }}
                    </v-icon>
                  </v-avatar>

                  <div class="d-flex flex-column text-right">
                    <span class="font-weight-bold text-caption text-truncate" style="max-width: 140px;">
                      {{ sku.title || sku.sku || `تنوع ${index + 1}` }}
                    </span>
                    <span
                      class="font-weight-bold"
                      :style="{ fontSize: '10px', color: getSkuStatus(sku).textColor }"
                    >
                      {{ getSkuStatus(sku).label }}
                    </span>
                  </div>
                </div>
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeSkuTab" class="sku-tab-content rounded-b-lg pa-4">
              <v-tab-item v-for="(sku, index) in localSkus" :key="sku.id">
                <div class="d-flex align-center justify-space-between mb-3 border-bottom pb-2">
                  <div class="d-flex align-center">
                    <v-chip color="primary" x-small class="ml-2 font-weight-bold">
                      #{{ index + 1 }}
                    </v-chip>
                    <span class="font-weight-bold text-subtitle-2 text-dark">
                      {{ sku.title || sku.sku }}
                    </span>

                    <v-chip
                      x-small
                      outlined
                      class="mr-2 font-weight-bold"
                      :color="getSkuStatus(sku).textColor"
                    >
                      <v-icon left x-small>{{ getSkuStatus(sku).icon }}</v-icon>
                      {{ getSkuStatus(sku).label }}
                    </v-chip>
                  </div>

                  <PriceCalculator
                    :product-id="payload.product_id"
                    :sku-id="sku.id"
                    :form-data="{
                      package_quantity: sku.package_quantity || payload.package_quantity,
                      package_cost: sku.package_cost || payload.package_cost,
                      price_step: sku.price_step || payload.price_step,
                      shipping_method: sku.shipping_method || payload.shipping_method
                    }"
                  />
                </div>

                <v-row dense>
                  <!-- انتخاب تنوع دیجی‌کالا -->
                  <v-col cols="12" v-if="digikalaVariants.length > 0">
                    <v-autocomplete
                      ref="skuSelect"
                      v-model="sku.digikala_variant_id"
                      :items="digikalaVariants"
                      :loading="isLoadingVariants"
                      item-value="id"
                      item-text="title"
                      label="تنوع دیجی‌کالا (جستجو با نام یا کد)"
                      outlined
                      dense
                      clearable
                      required
                      :rules="[v => !!v || 'انتخاب تنوع دیجی‌کالا الزامی است']"
                      prepend-inner-icon="mdi-magnify"
                      placeholder="نام یا کد تنوع دیجی‌کالا را تایپ کنید..."
                    >
                      <!-- تنوع انتخاب‌شده داخل فیلد -->
                      <template v-slot:selection="{ item }">
                        <div class="d-flex align-center text-truncate py-1">
                          <v-avatar size="28" rounded="sm" class="elevation-1 border ml-2 flex-shrink-0" color="grey lighten-4">
                            <v-img v-if="getItemImage(item)" :src="getItemImage(item)" aspect-ratio="1" contain />
                            <v-icon v-else x-small color="grey lighten-1">mdi-image-outline</v-icon>
                          </v-avatar>
                          <v-chip x-small color="secondary" class="ml-1 font-weight-bold">DKPC-{{ item.id }}</v-chip>
                          <span class="text-truncate">{{ item.title }}</span>
                        </div>
                      </template>

                      <!-- گزینه‌های منوی بازشونده تنوع‌ها -->
                      <template v-slot:item="{ item }">
                        <v-list-item-avatar size="40" rounded="lg" class="elevation-1 border my-1 ml-2" color="grey lighten-4">
                          <v-img v-if="getItemImage(item)" :src="getItemImage(item)" aspect-ratio="1" contain>
                            <template #placeholder>
                              <v-row class="fill-height ma-0" align="center" justify="center">
                                <v-progress-circular indeterminate size="16" color="grey lighten-1"></v-progress-circular>
                              </v-row>
                            </template>
                          </v-img>
                          <v-icon v-else small color="grey lighten-1">mdi-image-outline</v-icon>
                        </v-list-item-avatar>

                        <v-list-item-content>
                          <v-list-item-title class="font-weight-medium text-body-2 mb-1">
                            {{ item.title }}
                          </v-list-item-title>
                          <v-list-item-subtitle class="d-flex align-center">
                            <v-chip x-small color="secondary" outlined class="font-mono font-weight-bold">
                              DKPC-{{ item.id }}
                            </v-chip>
                            <span v-if="item.color_title" class="text-caption mr-2 grey--text text--darken-1">
                              رنگ: {{ item.color_title }}
                            </span>
                            <span v-if="item.size_title" class="text-caption mr-2 grey--text text--darken-1">
                              سایز: {{ item.size_title }}
                            </span>
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-col>

                  <!-- روش ارسال تنوع (اختیاری) -->
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="sku.shipping_method"
                      label="روش ارسال"
                      :items="shipping_methods"
                      outlined
                      dense
                      clearable
                      placeholder="ارث‌بری از کل"
                    />
                  </v-col>

                  <!-- تعداد در بسته تنوع (اختیاری) -->
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model.number="sku.package_quantity"
                      label="تعداد در بسته"
                      outlined
                      dense
                      type="number"
                      min="1"
                      clearable
                      placeholder="ارث‌بری از کل"
                    />
                  </v-col>

                  <!-- هزینه هر بسته تنوع (اختیاری) -->
                  <v-col cols="12" md="4">
                    <PriceInput
                      v-model="sku.package_cost"
                      label="هزینه هر بسته"
                      outlined
                      dense
                      clearable
                      placeholder="ارث‌بری از کل"
                    />
                  </v-col>

                  <!-- گام قیمت تنوع (اختیاری) -->
                  <v-col cols="12" md="4">
                    <PriceInput
                      v-model="sku.price_step"
                      label="گام قیمت"
                      outlined
                      dense
                      clearable
                      placeholder="ارث‌بری از کل"
                    />
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </template>
      </v-form>
    </v-card-text>

    <v-card-actions class="grey lighten-4 pa-4 d-flex justify-end gap-2">
      <v-btn class="btn-delete rounded-lg" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
        انصراف
      </v-btn>
      <v-spacer/>
      <v-btn
        class="btn-get-code rounded-lg"
        :loading="isSubmitting"
        :disabled="!isValid"
        @click="submitForm"
      >
        <v-icon left>mdi-link-variant-plus</v-icon>
        تایید و اتصال
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import PriceInput from "@/view/pages/uiBeautifier/priceInput";
import PriceCalculator from "../../../components/PriceCalculator.vue";

export default {
  name: "LinkProductModal",
  components: {
    PriceInput,
    PriceCalculator,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeSkuTab: 0,
      shipping_methods: [
        { text: 'ارسال دیجی/فروشنده/سه ساعته', value: 'all' },
        { text: 'ارسال فروشنده / سه ساعته', value: 'both' }
      ],
      isValid: false,
      isSubmitting: false,
      
      skusLoading: false,
      localSkus: [],
      
      digikalaProducts: [],
      isLoadingProducts: false,
      searchProduct: null,
      searchTimeout: null,
      
      digikalaVariants: [],
      isLoadingVariants: false,

      payload: {
        digikala_product_id: null,
        product_id: this.product.id,
        package_quantity: 1,
        package_cost: 200000,
        shipping_method: "all",
        price_step: 20000,
      },
    };
  },
  watch: {
    searchProduct(val) {
      if (!val || val.length < 2) return;
      if (this.isLoadingProducts) return;

      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.fetchDigikalaProducts(val);
      }, 500);
    },
    // هنگام تغییر تب تنوع، اگر خالی بود خودکار باز شود
    activeSkuTab() {
      this.openSkuDropdownIfEmpty();
    }
  },
  async mounted() {
    if (this.product?.has_variants) {
      await this.fetchProductSkus();
    }
    await this.fetchDigikalaProducts('');
    await this.fetchData();
  },
  methods: {
      getItemImage(item) {
      if (!item) return "";
      
      // حالت اول: فیلد مستقیم
      if (typeof item.image_src === "string" && item.image_src) return item.image_src;
      if (typeof item.thumbnail === "string" && item.thumbnail) return item.thumbnail;

      // حالت دوم: آرایه images یا gallery
      const imgs = item.images || item.gallery;
      if (Array.isArray(imgs) && imgs.length > 0) {
        const first = imgs[0];
        if (typeof first === "string") return first;
        if (first && (first.url || first.src || first.path)) {
          return first.url || first.src || first.path;
        }
      }

      // حالت سوم: آبجکت metadata دیجی‌کالا
      if (item.metadata) {
        if (item.metadata.image) return item.metadata.image;
        if (item.metadata.img_url) return item.metadata.img_url;
      }

      return "";
    },

    async handleProductChange(newProductId) {
      if (newProductId) {
        await this.fetchDigikalaVariants(newProductId);
        this.openSkuDropdownIfEmpty();
      } else {
        this.digikalaVariants = [];
      }
    },
    
    async fetchData() {
      try {
        const res = await this.$api.get(`/v1/marketplace-listings/digi/${this.product.id}/links`);
        const responseData = res?.data?.data;
        if (!responseData) {
          this.openProductDropdown();
          return;
        }

        if (responseData.defaults) {
          this.payload.package_quantity = responseData.defaults.package_quantity ?? this.payload.package_quantity;
          this.payload.package_cost = responseData.defaults.package_cost ?? this.payload.package_cost;
          this.payload.price_step = responseData.defaults.price_step ?? this.payload.price_step;
          this.payload.shipping_method = responseData.defaults.shipping_method ?? this.payload.shipping_method;
        }

        if (responseData.digikala_product_id) {
          const dkpId = Number(responseData.digikala_product_id);
          this.payload.digikala_product_id = dkpId;

          const existingProd = this.digikalaProducts.find(p => p.id === dkpId);
          if (!existingProd) {
            const title = responseData.listings?.[0]?.metadata?.title || `محصول ${dkpId}`;
            this.digikalaProducts.unshift({ id: dkpId, title });
          }

          await this.fetchDigikalaVariants(dkpId);
        } else {
          this.openProductDropdown();
        }

        const listings = responseData.listings || [];

        if (this.product?.has_variants && listings.length > 0) {
          const listingsMap = new Map();
          listings.forEach(item => {
            if (item.product_sku_id) {
              listingsMap.set(Number(item.product_sku_id), item);
            }
          });

          this.localSkus = await Promise.all(this.localSkus.map(async sku => {
            const match = listingsMap.get(Number(sku.id));
            if (!match) return sku;

            const variantId = match.external_variant_id ? Number(match.external_variant_id) : null;
            
            if (variantId && !this.digikalaVariants.some(v => v.id === variantId)) {
              this.digikalaVariants.push({
                id: variantId,
                title: match.metadata?.color || match.metadata?.title || `تنوع ${variantId}`
              });
            }

            let finalShippingMethod = match.shipping_method;

            if (variantId && !finalShippingMethod) {
              try {
                const res2 = await this.$api.get(`/v1/marketplace-listings/digi/get-variant`, {
                  params: { variant_id: variantId }
                });
                const variantData = res2.data?.data?.variant;
                if (variantData) {
                  const calcCondition = (variantData.is_fbd_active & variantData.is_fbs_active & variantData.is_three_hour_delivery_active) === 0;
                  finalShippingMethod = calcCondition ? 'both' : 'all';
                }
              } catch (err) {
                console.error(`خطا در دریافت روش ارسال تنوع ${variantId}:`, err);
                finalShippingMethod = 'all'; 
              }
            }

            return {
              ...sku,
              digikala_variant_id: variantId,
              package_quantity: match.package_quantity !== undefined ? match.package_quantity : null,
              package_cost: match.package_cost !== undefined ? match.package_cost : null,
              price_step: match.price_step !== undefined ? match.price_step : null,
              shipping_method: finalShippingMethod !== undefined ? finalShippingMethod : null
            };
          }));

          // اگر محصول متصل بود ولی تنوع فعلی تب جاری خالی بود، منوی تنوع باز شود
          this.openSkuDropdownIfEmpty();
        } 
        else if (!this.product?.has_variants && listings.length > 0) {
          const firstListing = listings[0];
          if (firstListing?.external_variant_id && !this.payload.shipping_method) {
            try {
              const res2 = await this.$api.get(`/v1/marketplace-listings/digi/get-variant`, {
                params: { variant_id: firstListing.external_variant_id }
              });
              const variantData = res2.data?.data?.variant;
              if (variantData) {
                const calcCondition = (variantData.is_fbd_active & variantData.is_fbs_active & variantData.is_three_hour_delivery_active) === 0;
                this.payload.shipping_method = calcCondition ? 'both' : 'all';
              }
            } catch (err) {
              console.error("خطا در دریافت روش ارسال محصول اصلی:", err);
            }
          }
        }
      } catch (error) {
        console.error("خطا در دریافت اطلاعات اتصال محصول:", error);
        this.openProductDropdown();
      }
    },

    openProductDropdown() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.payload.digikala_product_id && this.$refs.productSelect) {
            this.$refs.productSelect.focus();
            this.$refs.productSelect.isMenuActive = true;
          }
        }, 200);
      });
    },

    openSkuDropdownIfEmpty() {
      if (!this.product?.has_variants || !this.digikalaVariants.length) return;

      this.$nextTick(() => {
        setTimeout(() => {
          const currentSku = this.localSkus[this.activeSkuTab];
          if (currentSku && !currentSku.digikala_variant_id) {
            const skuRefs = this.$refs.skuSelect;
            const targetSelect = Array.isArray(skuRefs) ? skuRefs[this.activeSkuTab] : skuRefs;
            if (targetSelect) {
              targetSelect.focus();
              targetSelect.isMenuActive = true;
            }
          }
        }, 250);
      });
    },

    getSkuStatus(sku) {
      if (!sku.digikala_variant_id) {
        return {
          label: 'متصل نشده',
          icon: 'mdi-link-variant-off',
          color: 'grey darken-1',
          bgColor: 'grey lighten-2',
          textColor: '#757575',
        };
      }
      const hasCustomOverrides = !!(
        (sku.package_quantity !== null && sku.package_quantity !== '') ||
        (sku.package_cost !== null && sku.package_cost !== '') ||
        (sku.price_step !== null && sku.price_step !== '') ||
        (sku.shipping_method !== null && sku.shipping_method !== '')
      );

      if (hasCustomOverrides) {
        return {
          label: 'تنظیم دستی',
          icon: 'mdi-tune-vertical',
          color: 'amber darken-3',
          bgColor: 'amber lighten-4',
          textColor: '#e65100',
        };
      }

      return {
        label: 'ارث‌بری از کل',
        icon: 'mdi-lightning-bolt',
        color: 'blue darken-2',
        bgColor: 'blue lighten-4',
        textColor: '#1976d2',
      };
    },

    async fetchProductSkus() {
      this.skusLoading = true;
      try {
        const { data } = await this.$api.get(`/v1/products/${this.product.id}/skus`);
        const result = data?.data?.data || data?.data || data || [];
        
        this.localSkus = result.map(sku => ({
          ...sku,
          digikala_variant_id: null,
          package_quantity: null,
          package_cost: null,
          price_step: null,
          shipping_method: null
        }));
      } catch (error) {
        this.$toast?.error("خطا در دریافت لیست تنوع‌های محصول");
      } finally {
        this.skusLoading = false;
      }
    },

    async fetchDigikalaProducts(searchQuery) {
      this.isLoadingProducts = true;
      try {
        const { data } = await this.$api.get(`/v1/digikala/products/list`, {
          params: { search: searchQuery }
        });
        this.digikalaProducts = data?.data?.data || data?.data || data || [];
      } catch (error) {
        console.error("خطا در جستجوی محصولات دیجی‌کالا", error);
      } finally {
        this.isLoadingProducts = false;
      }
    },

    async fetchDigikalaVariants(digikalaProductId) {
      this.isLoadingVariants = true;
      try {
        const { data } = await this.$api.get(`/v1/digikala/variants/list`, {
          params: { product_id: digikalaProductId } 
        });
        this.digikalaVariants = data?.data?.data || data?.data || data || [];
      } catch (error) {
        this.$toast?.error("خطا در دریافت لیست تنوع‌های دیجی‌کالا");
      } finally {
        this.isLoadingVariants = false;
      }
    },

    async submitForm() {
      if (!this.$refs.form.validate()) return;

      const finalPayload = {
        product_id: this.payload.product_id,
        digikala_product_id: Number(this.payload.digikala_product_id),
        package_quantity: Number(this.payload.package_quantity),
        package_cost: Number(String(this.payload.package_cost).replace(/,/g, '')),
        shipping_method: this.payload.shipping_method,
        price_step: Number(String(this.payload.price_step).replace(/,/g, '')),
      };

      if (this.product?.has_variants) {
        const variant_mappings = this.localSkus
          .filter(sku => sku.digikala_variant_id)
          .map(sku => {
            const mapping = {
              product_sku_id: sku.id,
              digikala_variant_id: Number(sku.digikala_variant_id),
            };

            if (sku.package_quantity) {
              mapping.package_quantity = Number(sku.package_quantity);
            }
            if (sku.package_cost) {
              mapping.package_cost = Number(String(sku.package_cost).replace(/,/g, ''));
            }
            if (sku.price_step) {
              mapping.price_step = Number(String(sku.price_step).replace(/,/g, ''));
            }
            if (sku.shipping_method) {
              mapping.shipping_method = sku.shipping_method;
            }

            return mapping;
          });

        if (variant_mappings.length === 0) {
          this.$toast?.warning("لطفاً تنوع دیجی‌کالا را حداقل برای یکی از تنوع‌های محصول انتخاب کنید.");
          return;
        }

        finalPayload.variant_mappings = variant_mappings;
      }

      this.isSubmitting = true;
      try {
        const response = await this.$api.post('/v1/marketplace-listings/digi/link-product', finalPayload);

        if (response.data?.status || response.status === 200) {
          this.$toast?.success(response.data?.message || "محصول با موفقیت متصل شد.");
          this.$emit('saved');
          this.$emit('close');
        }
      } catch (error) {
        this.$toast?.error(error.response?.data?.message || "خطا در اتصال محصول");
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.sku-tab-content {
  background-color: #fafbfc !important;
  border: 1px solid #e2e8f0;
  border-top: none !important;
}
.border-bottom {
  border-bottom: 1px solid #edf2f7;
}
.gap-2 {
  gap: 8px;
}
</style>
